import { supabase } from '@/integrations/supabase/client';

export interface PolicyDependencyReport {
  policyId: string;
  policyName: string;
  canDelete: boolean;
  dependencies: {
    activeAssignments: {
      count: number;
      details: Array<{ profile_id: string; employee_name: string; start_date: string }>;
    };
    leaveRequests: {
      count: number;
      details: Array<{ id: string; employee_name: string; status: string; start_date: string; end_date: string }>;
    };
    leaveBalances: {
      count: number;
      details: Array<{ profile_id: string; employee_name: string; current_balance_days: number; pending_days: number }>;
    };
    accrualHistory: {
      count: number;
      hasRecords: boolean;
    };
  };
  totalDependencies: number;
  blockingDependencies: number;
}

export const policyDependencyService = {
  /**
   * Check all dependencies for a policy before deletion
   */
  async checkPolicyDependencies(policyId: string): Promise<PolicyDependencyReport> {
    try {
      // Get policy details
      const { data: policy, error: policyError } = await supabase
        .from('leave_policies')
        .select('name')
        .eq('id', policyId)
        .single();

      if (policyError) throw policyError;

      // Check all dependencies in parallel
      const [
        activeAssignments,
        leaveRequests,
        leaveBalances,
        accrualHistory
      ] = await Promise.all([
        this.checkActiveAssignments(policyId),
        this.checkLeaveRequests(policyId),
        this.checkLeaveBalances(policyId),
        this.checkAccrualHistory(policyId)
      ]);

      const totalDependencies = activeAssignments.count + leaveRequests.count + leaveBalances.count + (accrualHistory.hasRecords ? 1 : 0);
      
      // Blocking dependencies are active assignments and pending/approved leave requests
      const blockingDependencies = activeAssignments.count + 
        leaveRequests.details.filter(req => req.status === 'pending' || req.status === 'approved').length;

      return {
        policyId,
        policyName: policy.name,
        canDelete: blockingDependencies === 0,
        dependencies: {
          activeAssignments,
          leaveRequests,
          leaveBalances,
          accrualHistory
        },
        totalDependencies,
        blockingDependencies
      };
    } catch (error) {
      console.error('[Policy Dependency Service] Error checking dependencies:', error);
      throw new Error(`Failed to check policy dependencies: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Check for active employee assignments
   */
  async checkActiveAssignments(policyId: string) {
    const { data, error } = await supabase
      .from('policy_employee_assignments')
      .select(`
        profile_id,
        start_date
      `)
      .eq('policy_id', policyId)
      .eq('is_active', true);

    if (error) throw error;

    // Get profile names separately to avoid join issues
    const profileIds = data?.map(a => a.profile_id) || [];
    let profiles: any[] = [];
    
    if (profileIds.length > 0) {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', profileIds);
      
      if (!profileError) {
        profiles = profileData || [];
      }
    }

    return {
      count: data?.length || 0,
      details: data?.map(assignment => {
        const profile = profiles.find(p => p.id === assignment.profile_id);
        return {
          profile_id: assignment.profile_id,
          employee_name: profile ? `${profile.first_name} ${profile.last_name}` : 'Unknown Employee',
          start_date: assignment.start_date
        };
      }) || []
    };
  },

  /**
   * Check for existing leave requests (any status)
   */
  async checkLeaveRequests(policyId: string) {
    const { data, error } = await supabase
      .from('leave_requests')
      .select(`
        id,
        profile_id,
        status,
        start_date,
        end_date
      `)
      .eq('policy_id', policyId);

    if (error) throw error;

    // Get profile names separately to avoid join issues
    const profileIds = data?.map(r => r.profile_id) || [];
    let profiles: any[] = [];
    
    if (profileIds.length > 0) {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', profileIds);
      
      if (!profileError) {
        profiles = profileData || [];
      }
    }

    return {
      count: data?.length || 0,
      details: data?.map(request => {
        const profile = profiles.find(p => p.id === request.profile_id);
        return {
          id: request.id,
          employee_name: profile ? `${profile.first_name} ${profile.last_name}` : 'Unknown Employee',
          status: request.status,
          start_date: request.start_date,
          end_date: request.end_date
        };
      }) || []
    };
  },

  /**
   * Check for leave balances with current or carried balances
   */
  async checkLeaveBalances(policyId: string) {
    const { data, error } = await supabase
      .from('leave_balances')
      .select(`
        profile_id,
        current_balance_days,
        pending_days
      `)
      .eq('policy_id', policyId)
      .or('current_balance_days.gt.0,pending_days.gt.0,carried_over_days.gt.0');

    if (error) throw error;

    // Get profile names separately to avoid join issues
    const profileIds = data?.map(b => b.profile_id) || [];
    let profiles: any[] = [];
    
    if (profileIds.length > 0) {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', profileIds);
      
      if (!profileError) {
        profiles = profileData || [];
      }
    }

    return {
      count: data?.length || 0,
      details: data?.map(balance => {
        const profile = profiles.find(p => p.id === balance.profile_id);
        return {
          profile_id: balance.profile_id,
          employee_name: profile ? `${profile.first_name} ${profile.last_name}` : 'Unknown Employee',
          current_balance_days: balance.current_balance_days,
          pending_days: balance.pending_days
        };
      }) || []
    };
  },

  /**
   * Check for accrual history records
   */
  async checkAccrualHistory(policyId: string) {
    const { count, error } = await supabase
      .from('accrual_history')
      .select('id', { count: 'exact', head: true })
      .eq('policy_id', policyId);

    if (error) throw error;

    return {
      count: count || 0,
      hasRecords: (count || 0) > 0
    };
  },

  /**
   * Generate detailed dependency message for UI
   */
  generateDependencyMessage(report: PolicyDependencyReport): string {
    if (report.canDelete) {
      return `Policy "${report.policyName}" can be safely deactivated.`;
    }

    const messages: string[] = [];
    
    if (report.dependencies.activeAssignments.count > 0) {
      messages.push(`${report.dependencies.activeAssignments.count} active employee assignment(s)`);
    }
    
    const pendingApproved = report.dependencies.leaveRequests.details.filter(
      req => req.status === 'pending' || req.status === 'approved'
    );
    if (pendingApproved.length > 0) {
      messages.push(`${pendingApproved.length} pending/approved leave request(s)`);
    }

    return `Cannot deactivate policy "${report.policyName}" due to: ${messages.join(', ')}.`;
  }
};